import { useMemo } from 'react';
import { DemeterSymbolModel } from '../../../Generated/Raven-Demeter';
import Carousel from '../../Components/Carousel/Carousel';
import useSearchParameters from '../../Components/Navigation/Hooks/useSearchParametersHook';
import { TrackingEvent } from '../../Services/Tracking/TrackingService';
import CumulativeValueAtRiskChart from './Charts/CumulativeValueAtRiskChart';
import ForwardCurveAndBasisChart from './Charts/FowardCurveAndBasisChart';
import MarkToMarketChart from './Charts/MarkToMarketChart';
import MonthlyNetValueAtRiskChart from './Charts/MonthlyNetValueAtRiskChart';
import NetPositionChart from './Charts/NetPositionChart';
import NetPositionCumulativeBudgetAtRiskChart from './Charts/NetPositionCumulativeBudgetAtRiskChart';
import NetPositionMonthlyBudgetAtRiskChart from './Charts/NetPositionMonthlyBudgetAtRiskChart';
import ValueAtRiskAssumptionsChart from './Charts/ValueAtRiskAssumptionsChart';
import { MonthlyRiskCompositeModel, RiskRequest } from './RiskDefinitions';
import styles from './RiskPage.module.scss';

interface IRiskCarouselProps {
    symbol: DemeterSymbolModel;
    risksRequest: RiskRequest;
    calculatedRisks: MonthlyRiskCompositeModel[];
    displayDecimalPlaces: number;
    track: TrackingEvent;
}

const RiskCarousel: React.FC<IRiskCarouselProps> = (props: IRiskCarouselProps) => {
    const [searchParamters] = useSearchParameters();

    const getDateRangeOfInputs = useMemo(() => {
        const lastValidRiskCompositeModel = props.calculatedRisks.reduce((previousValue, currentValue) => {
            if (currentValue.budgetPosition) {
                return currentValue;
            }

            return previousValue;
        }, {} as MonthlyRiskCompositeModel);

        const lastValidIndex = props.calculatedRisks.findIndex(
            (x) => x.contractYear === lastValidRiskCompositeModel.contractYear && x.contractMonth === lastValidRiskCompositeModel.contractMonth,
        );

        if (lastValidIndex < 0) {
            return props.calculatedRisks;
        }
        return props.calculatedRisks.slice(0, lastValidIndex + 1);
    }, [props.calculatedRisks]);

    return (
        <div className={styles.risk_carousel}>
            <Carousel track={{ eventCategory: 'RiskCalculator', eventAction: 'CarouselChartChanged' }}>
                <div className={styles.risk_carousel_chart}>
                    <ValueAtRiskAssumptionsChart
                        symbol={props.symbol}
                        risksRequest={props.risksRequest}
                        calculatedRisks={getDateRangeOfInputs}
                        displayDecimalPlaces={props.displayDecimalPlaces}
                        track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}ValueAtRiskAssumptionsChart` }}
                    />
                </div>
                <div className={styles.risk_carousel_chart}>
                    <CumulativeValueAtRiskChart
                        symbol={props.symbol}
                        risksRequest={props.risksRequest}
                        calculatedRisks={getDateRangeOfInputs}
                        track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}CumulativeValueAtRiskChart` }}
                    />
                </div>
                <div className={styles.risk_carousel_chart}>
                    <ForwardCurveAndBasisChart
                        symbol={props.symbol}
                        risksRequest={props.risksRequest}
                        calculatedRisks={getDateRangeOfInputs}
                        displayDecimalPlaces={props.displayDecimalPlaces}
                        track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}ForwardCurveAndBasisChart` }}
                    />
                </div>
                <div className={styles.risk_carousel_chart}>
                    <MarkToMarketChart
                        symbol={props.symbol}
                        risksRequest={props.risksRequest}
                        calculatedRisks={getDateRangeOfInputs}
                        track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}MarkToMarketChart` }}
                    />
                </div>
                <div className={styles.risk_carousel_chart}>
                    <MonthlyNetValueAtRiskChart
                        symbol={props.symbol}
                        risksRequest={props.risksRequest}
                        calculatedRisks={getDateRangeOfInputs}
                        track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}MonthlyNetValueAtRiskChart` }}
                    />
                </div>
                <div className={styles.risk_carousel_chart}>
                    <NetPositionChart
                        symbol={props.symbol}
                        risksRequest={props.risksRequest}
                        calculatedRisks={getDateRangeOfInputs}
                        track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}NetPositionChart` }}
                    />
                </div>
                {searchParamters.tab === 'budget' && (
                    <div className={styles.risk_carousel_chart}>
                        <NetPositionMonthlyBudgetAtRiskChart
                            symbol={props.symbol}
                            risksRequest={props.risksRequest}
                            calculatedRisks={getDateRangeOfInputs}
                            track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}NetPositionMonthlyBudgetAtRiskChart` }}
                        />
                    </div>
                )}
                {searchParamters.tab === 'budget' && (
                    <div className={styles.risk_carousel_chart}>
                        <NetPositionCumulativeBudgetAtRiskChart
                            symbol={props.symbol}
                            risksRequest={props.risksRequest}
                            calculatedRisks={getDateRangeOfInputs}
                            track={{
                                eventCategory: props.track.eventCategory,
                                eventAction: `${props.track.eventAction}NetPositionCumulativeBudgetAtRiskChart`,
                            }}
                        />
                    </div>
                )}
            </Carousel>
        </div>
    );
};

export default RiskCarousel;
